/**
 * Career Map Restriction Utilities
 * 
 * Handles filtering and access control for career maps based on user's plan
 */

/**
 * Check if user can access a specific career category
 * @param {Array|string} allowedCategories - Allowed categories from plan ('all' or array of categories)
 * @param {string} categorySlug - Category slug to check
 * @returns {boolean}
 */
export const canAccessCategory = (allowedCategories, categorySlug) => {
  if (!allowedCategories) return false;
  
  // If 'all' is in the array or allowedCategories is 'all', allow everything
  if (allowedCategories === 'all' || 
      (Array.isArray(allowedCategories) && allowedCategories.includes('all'))) {
    return true;
  }
  
  // Check if specific category is allowed
  if (Array.isArray(allowedCategories)) {
    return allowedCategories.includes(categorySlug);
  }
  
  return false;
};

/**
 * Check if user can access a specific career subcategory (map)
 * @param {Array|string} allowedCategories - Allowed categories from plan
 * @param {Array|string} allowedSubcategories - Allowed subcategories from plan ('all' or array)
 * @param {string} categorySlug - Parent category slug
 * @param {string} subcategorySlug - Subcategory/map slug to check
 * @returns {boolean}
 */
export const canAccessSubcategory = (
  allowedCategories, 
  allowedSubcategories, 
  categorySlug, 
  subcategorySlug
) => {
  // First check if category is accessible
  if (!canAccessCategory(allowedCategories, categorySlug)) {
    return false;
  }
  
  // If subcategories is 'all', allow all maps in accessible categories
  if (allowedSubcategories === 'all') {
    return true;
  }
  
  // Check if specific subcategory is allowed
  if (Array.isArray(allowedSubcategories)) {
    return allowedSubcategories.includes(subcategorySlug);
  }
  
  return false;
};

/**
 * Filter career categories based on plan restrictions
 * @param {Array} categories - All available categories
 * @param {Array|string} allowedCategories - Allowed categories from plan
 * @returns {Array} Filtered categories
 */
export const filterCategories = (categories, allowedCategories) => {
  if (!categories || !Array.isArray(categories)) return [];
  
  // If 'all' is allowed, return all categories
  if (allowedCategories === 'all' || 
      (Array.isArray(allowedCategories) && allowedCategories.includes('all'))) {
    return categories;
  }
  
  // Filter categories based on allowed list
  if (Array.isArray(allowedCategories)) {
    return categories.filter(category => 
      allowedCategories.includes(category.slug)
    );
  }
  
  return [];
};

/**
 * Filter career subcategories (maps) based on plan restrictions
 * @param {Array} subcategories - All available subcategories
 * @param {string} categorySlug - Parent category slug
 * @param {Array|string} allowedCategories - Allowed categories from plan
 * @param {Array|string} allowedSubcategories - Allowed subcategories from plan
 * @returns {Array} Filtered subcategories
 */
export const filterSubcategories = (
  subcategories, 
  categorySlug,
  allowedCategories, 
  allowedSubcategories
) => {
  if (!subcategories || !Array.isArray(subcategories)) return [];
  
  // Check if category is accessible
  if (!canAccessCategory(allowedCategories, categorySlug)) {
    return [];
  }
  
  // If 'all' subcategories are allowed, return all
  if (allowedSubcategories === 'all') {
    return subcategories;
  }
  
  // Filter subcategories based on allowed list
  if (Array.isArray(allowedSubcategories)) {
    return subcategories.filter(subcategory => 
      allowedSubcategories.includes(subcategory.slug)
    );
  }
  
  return [];
};

/**
 * Get restriction info for a category
 * @param {string} categorySlug - Category slug
 * @param {Array|string} allowedCategories - Allowed categories from plan
 * @returns {Object} { isRestricted: boolean, reason: string }
 */
export const getCategoryRestrictionInfo = (categorySlug, allowedCategories) => {
  const hasAccess = canAccessCategory(allowedCategories, categorySlug);
  
  return {
    isRestricted: !hasAccess,
    reason: hasAccess 
      ? null 
      : 'This category is not available in your current plan. Upgrade to access more career paths!'
  };
};

/**
 * Get restriction info for a subcategory (map)
 * @param {string} categorySlug - Parent category slug
 * @param {string} subcategorySlug - Subcategory slug
 * @param {Array|string} allowedCategories - Allowed categories from plan
 * @param {Array|string} allowedSubcategories - Allowed subcategories from plan
 * @returns {Object} { isRestricted: boolean, reason: string }
 */
export const getSubcategoryRestrictionInfo = (
  categorySlug,
  subcategorySlug,
  allowedCategories,
  allowedSubcategories
) => {
  const hasAccess = canAccessSubcategory(
    allowedCategories, 
    allowedSubcategories, 
    categorySlug, 
    subcategorySlug
  );
  
  return {
    isRestricted: !hasAccess,
    reason: hasAccess 
      ? null 
      : 'This career map is not available in your current plan. Upgrade to unlock!'
  };
};

/**
 * Count accessible items
 * @param {Array} items - All items
 * @param {Array|string} allowedCategories - Allowed categories
 * @param {Array|string} allowedSubcategories - Allowed subcategories (optional)
 * @param {string} categorySlug - Category slug (for subcategories)
 * @returns {Object} { total: number, accessible: number, restricted: number }
 */
export const countAccessibleItems = (
  items, 
  allowedCategories, 
  allowedSubcategories = null,
  categorySlug = null
) => {
  if (!items || !Array.isArray(items)) {
    return { total: 0, accessible: 0, restricted: 0 };
  }
  
  const total = items.length;
  let accessible = 0;
  
  if (categorySlug && allowedSubcategories !== null) {
    // Counting subcategories
    accessible = items.filter(item => 
      canAccessSubcategory(allowedCategories, allowedSubcategories, categorySlug, item.slug)
    ).length;
  } else {
    // Counting categories
    accessible = items.filter(item => 
      canAccessCategory(allowedCategories, item.slug)
    ).length;
  }
  
  return {
    total,
    accessible,
    restricted: total - accessible
  };
};

/**
 * Check if plan has full access (all categories and subcategories)
 * @param {Array|string} allowedCategories - Allowed categories from plan
 * @param {Array|string} allowedSubcategories - Allowed subcategories from plan
 * @returns {boolean}
 */
export const hasFullAccess = (allowedCategories, allowedSubcategories) => {
  const categoriesAll = allowedCategories === 'all' || 
    (Array.isArray(allowedCategories) && allowedCategories.includes('all'));
  
  const subcategoriesAll = allowedSubcategories === 'all';
  
  return categoriesAll && subcategoriesAll;
};

/**
 * Get plan access level description
 * @param {Array|string} allowedCategories - Allowed categories from plan
 * @param {Array|string} allowedSubcategories - Allowed subcategories from plan
 * @returns {string}
 */
export const getAccessLevelDescription = (allowedCategories, allowedSubcategories) => {
  if (hasFullAccess(allowedCategories, allowedSubcategories)) {
    return 'Full Access - All career maps unlocked';
  }
  
  if (allowedSubcategories === 'all') {
    const categoryCount = Array.isArray(allowedCategories) ? allowedCategories.length : 0;
    return `Limited Categories - ${categoryCount} categories with all maps`;
  }
  
  const categoryCount = Array.isArray(allowedCategories) ? allowedCategories.length : 0;
  const subcategoryCount = Array.isArray(allowedSubcategories) ? allowedSubcategories.length : 0;
  
  return `Limited Access - ${categoryCount} categories, ${subcategoryCount} career maps`;
};

export default {
  canAccessCategory,
  canAccessSubcategory,
  filterCategories,
  filterSubcategories,
  getCategoryRestrictionInfo,
  getSubcategoryRestrictionInfo,
  countAccessibleItems,
  hasFullAccess,
  getAccessLevelDescription
};
